import { formatCalibrationDttmUTC, normalizeCalibrationDttm } from "../util";

export const STRUCTURE_MILES_DATASET_TYPE = "structure-miles";
export const ROOT_GEOJSON_DATASET_TYPE = "root-geojson";

/** Matches the existing `{minSensors ?? "—"}` convention in SensorCountsContent. */
export const EM_DASH = "—";

/**
 * Format a mileage value for display.
 *
 * Cal-plat emits miles as full-precision floats (e.g. 1234.5678901234). One decimal is
 * the most precision that is meaningful for a corridor length and the most that fits the
 * 280px sidebar column; thousands separators because real projects run to four figures.
 * The locale is pinned so Jest assertions do not depend on the runner's locale.
 */
export const formatMiles = (value) =>
    typeof value === "number" && Number.isFinite(value)
        ? value.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : EM_DASH;

/** Format an integer count with thousands separators. */
export const formatCount = (value) =>
    typeof value === "number" && Number.isFinite(value) ? value.toLocaleString("en-US") : EM_DASH;

/**
 * Pull a human-readable reason out of a failed cal-plat request.
 *
 * Cal-plat's error middleware does not use a single key: a ClientError becomes
 * {"client_error": ...} 404, a KeyError {"key_error": ...} 400, a ValueError
 * {"value_error": ...} 400, a TypeError {"type_error": ...} 400, and only the catch-all
 * uses {"error": ...}. The likeliest real failure for structure miles is a ValueError
 * (a missing dataset, or a required input submitted without a version_dttm), so reading
 * only `.error` would degrade to axios's generic "Request failed with status code 400".
 */
export const extractCalPlatError = (err) => {
    const data = err?.response?.data;
    return (
        data?.error ??
        data?.value_error ??
        data?.key_error ??
        data?.type_error ??
        data?.client_error ??
        data?.message ??
        err?.message ??
        String(err)
    );
};

const rootGeojsonInputVersion = (meta) => {
    // input_datasets serializes dataset_type by NAME ("RootGeojson") because
    // DatasetKey.dataset_type does not set marshmallow's by_value flag, while the
    // top-level meta.dataset_type does (so that one is "structure-miles"). Match both
    // spellings so this keeps working if that ever gets normalized.
    const inputs = Array.isArray(meta?.input_datasets) ? meta.input_datasets : [];
    const match = inputs.find(
        (input) => input?.dataset_type === "RootGeojson" || input?.dataset_type === ROOT_GEOJSON_DATASET_TYPE
    );
    return match?.version_dttm ?? null;
};

/**
 * Work out which RootGeojson a structure-miles dataset was computed from.
 *
 * Two shapes exist, and the label shown to the user differs between them:
 *
 * - A standalone StructureMilesFromRootGeojson run reads a RootGeojson as an input, so
 *   the version is in meta.input_datasets. `fromInput` is true.
 * - A dataset emitted as a secondary output of a RootGeojson producer has no RootGeojson
 *   *input* (the RootGeojson was an output of that same run). base_runtime stamps every
 *   output of a run with one generated_at, so the structure-miles version_dttm IS the
 *   RootGeojson's. `fromInput` is false.
 *
 * Returns null when there is nothing to report, so callers can skip the line entirely
 * rather than rendering a half-empty label.
 */
export const resolveSourceRootGeojsonVersion = (meta) => {
    if (!meta) return null;
    const inputVersion = rootGeojsonInputVersion(meta);
    if (inputVersion) {
        return { version: normalizeCalibrationDttm(inputVersion), fromInput: true };
    }
    const runVersion = meta.version_dttm;
    if (!runVersion) return null;
    return { version: normalizeCalibrationDttm(runVersion), fromInput: false };
};

/**
 * The one-line "where did these numbers come from" caption, shared by the card and the dialog
 * so the two can never disagree. Returns null when the source is unknown, so callers skip the
 * line rather than rendering a dangling label.
 *
 * Both wordings are literally true for their branch: a standalone run really did read that
 * RootGeojson as an input, and a piggybacked dataset really was computed at that moment from
 * the RootGeojson written by the same run.
 */
export const formatProvenance = (meta) => {
    const source = resolveSourceRootGeojsonVersion(meta);
    const when = formatCalibrationDttmUTC(source?.version);
    if (!when) return null;
    return source.fromInput ? `From root-geojson ${when}` : `Computed ${when}`;
};

/**
 * True when a RootGeojson newer than the one behind these miles exists.
 *
 * Both sides are normalized first, which is the whole point: meta.version_dttm comes back
 * microsecond+offset ("2026-04-15T17:10:09.997415+00:00") while dataset file basenames are
 * second-precision Z ("2026-07-21T15:00:00Z"), so a raw string compare would report every
 * dataset as stale. Once normalized, both are fixed-width Z timestamps and a lexical
 * compare is a chronological one.
 *
 * Returns false whenever either side is unknown — a missing signal must never render as an
 * alarm.
 */
export const isStructureMilesStale = (meta, latestRootGeojsonVersion) => {
    const source = resolveSourceRootGeojsonVersion(meta);
    const latest = normalizeCalibrationDttm(latestRootGeojsonVersion);
    if (!source?.version || !latest) return false;
    return latest > source.version;
};

const isBlank = (value) =>
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "number" && !Number.isFinite(value));

/**
 * Sort by_line rows by any column.
 *
 * Copies before sorting because the incoming array belongs to the hook's state and must not
 * be reordered in place. Blank values (an unnamed line, an unknown voltage) sort last in
 * BOTH directions, so reversing the sort never pushes the rows the user cares about below a
 * block of placeholders.
 */
export const sortLineRows = (rows, key, direction = "asc") => {
    if (!Array.isArray(rows)) return [];
    return rows.slice().sort((a, b) => {
        const aValue = a?.[key];
        const bValue = b?.[key];
        const aBlank = isBlank(aValue);
        const bBlank = isBlank(bValue);
        if (aBlank && bBlank) return 0;
        if (aBlank) return 1;
        if (bBlank) return -1;
        const comparison =
            typeof aValue === "string" || typeof bValue === "string"
                ? String(aValue).localeCompare(String(bValue))
                : aValue - bValue;
        return direction === "desc" ? -comparison : comparison;
    });
};
